import React, { useCallback, useEffect, useRef, useState } from "react";
import JewelryInfoForm from "./JeweleryInfoForm";
import { ArrowUpFromLine } from "lucide-react";
import jewellery from "lib/jewellery-client";
import useLoad from "hook/useLoad";
import "./jewellery.css";

const MAX_HEIGHT = 300;

const ImageUploader = () => {
  const [file, setFile] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [imageType, setImageType] = useState("design");

  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [isLoading, triggerLoader] = useLoad();
  const textareaRef = useRef(null);

  //==================== File Handlers ====================//
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setError("Please select a file first.");
      return;
    }

    setError(null);
    setResult(null);

    await triggerLoader(async () => {
      try {
        const { base64Url } = await jewellery.base64Image({ file });

        const { data, error } = await jewellery.designExtract({
          image: base64Url,
          prompt: prompt.trim(),
          type: imageType,
        });

        if (error) {
          setError(error);
        } else {
          setResult(data);
        }
      } catch (err) {
        console.error(err);
        setError("An unexpected error occurred.");
      }
    });
  };

  //==================== Modal Handlers ====================//
  const handleHide = useCallback(() => setShowModal(false), []);
  const handleShow = useCallback(() => setShowModal(true), []);

  //==================== Auto-Resize Prompt ====================//
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    const newHeight = Math.min(textarea.scrollHeight, MAX_HEIGHT);
    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > MAX_HEIGHT ? "auto" : "hidden";
  }, [prompt]);

  //==================== Render ====================//
  const renderResultPanel = () => {
    if (isLoading) {
      return (
        <div className="results-panel">
          <div className="panel-header d-flex justify-content-between align-items-center">
            <h2 className="pt-2">Result</h2>
          </div>
          <div className="panel-content d-flex justify-content-center">
            <span className="loader1"></span>
          </div>
        </div>
      );
    }

    if (result?.data) {
      return (
        <div className="results-panel">
          <div className="panel-content">
            <button className="btn btn-primary btn-sm" onClick={handleShow}>
              View Form
            </button>

            <JewelryInfoForm
              data={result.data}
              type={result.type}
              show={showModal}
              onHide={handleHide}
            />

            <div className="json-output mt-4">
              <h5>Raw JSON Output:</h5>
              <pre
                style={{
                  backgroundColor: "#f6f8fa",
                  padding: "1rem",
                  borderRadius: "8px",
                  fontSize: "14px",
                  overflowX: "auto",
                  whiteSpace: "pre-wrap",
                }}
              >
                {JSON.stringify(result.data, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="empty-state">
        <p>No data available. Upload an image to extract jewelry information.</p>
      </div>
    );
  };

  return (
    <div className="app-container">
      {renderResultPanel()}

      {/* Upload Form */}
      <div className="upload-container">
        <div className="upload-box">
          <form className="upload-form" onSubmit={handleSubmit}>
            <div className="input-group-upload">
              <textarea
                ref={textareaRef}
                className="custom-input-promt"
                placeholder="Enter Prompt..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />

              <select
                value={imageType}
                onChange={(e) => setImageType(e.target.value)}
                className="form-select custom-type-select"
              >
                <option value="order">ORDER</option>
                <option value="design">DESIGN</option>
              </select>
            </div>

            <div className="input-group-upload">
              <input
                type="file"
                id="file-upload"
                onChange={handleFileChange}
                accept="image/*"
                className="file-input"
                disabled={isLoading}
              />
              <label htmlFor="file-upload" className="file-label">
                {file ? file.name : "Choose jewelry design image..."}
              </label>

              <button
                type="submit"
                className="upload-button"
                disabled={!file || isLoading}
                title={file ? "Upload image" : "Select an image first"}
              >
                {isLoading ? <div className="button-spinner" /> : <ArrowUpFromLine />}
              </button>
            </div>
          </form>

          {error && <div className="error-message">{error}</div>}
        </div>
      </div>
    </div>
  );
};

export default ImageUploader;
